// The vendor detail page headline. `company_name` is the "Vendor legal name" an
// admin typed into the create-vendor modal; `abn_entity_name` is what the ABR
// registry returned for the vendor's ABN. When they disagree the admin should
// see both without leaving the page, so the registry name is rendered in
// parentheses after the entered name.
//
// The entered name always leads and is echoed verbatim — it is the admin's own
// input, and the page's identity (avatar initials, page title) is built from it.
// The registry name is returned non-null ONLY on a genuine mismatch, so the
// caller renders parentheses without repeating the comparison.
//
// Comparison must agree with legalNameDisplay.ts, which decides the same
// "is this a mismatch" question for the Vendor information tab's Legal entity
// name row. If the two drift, one surface flags a discrepancy the other calls
// identical.

// Comparison only. Nothing is ever stripped from what gets displayed: the
// registry name is passed through verbatim, punctuation and legal suffix
// included. Deliberately no "Pty Ltd"/punctuation normalisation — that would
// quieten the page by hiding real differences, and the suffix is sometimes the
// only thing distinguishing two related entities.
const norm = (s: string | null): string => (s ?? '').trim().toLowerCase();
const present = (s: string | null): boolean => (s ?? '').trim() !== '';

export type CompanyHeading = {
  /** The admin-entered vendor legal name, verbatim. */
  name: string;
  /** The ABR registry name, non-null ONLY on a genuine mismatch. */
  registryName: string | null;
};

export function companyHeading(
  companyName: string,
  abnEntityName: string | null,
): CompanyHeading {
  // No registry name to compare against — the ABN was never checked, or the
  // stored value is blank. A blank-but-present value counts as absent.
  if (!present(abnEntityName)) return { name: companyName, registryName: null };

  // Pure formatting differences are not a discrepancy worth flagging, so
  // "Acme Couriers Pty Ltd" and "ACME COURIERS PTY LTD" read as the same name.
  if (norm(companyName) === norm(abnEntityName)) {
    return { name: companyName, registryName: null };
  }

  return { name: companyName, registryName: abnEntityName };
}
